import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";

const CAT_LABEL = {
  missing_information: "Missing information", unclear_answer: "Unclear answer",
  photo_issue: "Profile photo issue", identity_verification: "Identity verification",
  eligibility: "Eligibility question", other: "Other",
};
const STATUS_LABEL = {
  pending: "Pending review", under_review: "Under review",
  info_requested: "Awaiting your response", resubmitted: "Under renewed review",
  approved: "Approved", rejected: "Not approved",
};

export default function MyApplication() {
  const [data, setData] = useState(null);
  const [replies, setReplies] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const r = await api.get("/review/me/application"); setData(r.data); }
    catch (e) { toast.error("Failed to load your application"); }
  };
  useEffect(() => { load(); }, []);

  const respond = async (rid) => {
    const msg = (replies[rid] || "").trim();
    if (!msg) return toast.error("Please write a response");
    setBusy(rid);
    try {
      await api.post(`/review/me/application/requests/${rid}/respond`, { message: msg });
      toast.success("Response submitted for review");
      setReplies({ ...replies, [rid]: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#0C0A0A] px-4 py-10" data-testid="my-application-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/discover"><BrandLogo variant="wordmark" className="h-9 w-auto" /></Link>
          <Link to="/discover" className="text-sm text-[#9E978E] hover:text-[#D4AF37]">Back to Discover</Link>
        </div>
        <h1 className="font-serif text-3xl text-[#FBF8F3]">Your Application</h1>
        {data && (
          <div className="mt-2 inline-block text-xs uppercase tracking-wide px-3 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37]" data-testid="my-app-status">
            {STATUS_LABEL[data.status] || data.status}
          </div>
        )}

        {data && data.open_requests.length > 0 && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-[#9E978E]">A reviewer has requested the following. Update your profile/photos as needed, then reply below.</p>
            {data.open_requests.map((r) => (
              <div key={r.request_id} data-testid={`my-req-${r.request_id}`} className="rounded-2xl border border-[#E0B84A]/30 bg-[#1a1512]/60 p-5">
                <div className="text-xs uppercase tracking-wide text-[#E0B84A]">{CAT_LABEL[r.category] || r.category}</div>
                <div className="text-[#FBF8F3] mt-2">{r.message}</div>
                <div className="text-sm text-[#C9A96E] mt-2">What to do: {r.required_action}</div>
                {r.deadline && <div className="text-xs text-[#9E978E] mt-1">Please respond by {r.deadline}</div>}
                <textarea data-testid={`my-reply-${r.request_id}`} rows={3} value={replies[r.request_id] || ""}
                  onChange={(e) => setReplies({ ...replies, [r.request_id]: e.target.value })}
                  placeholder="Write your response / explanation…"
                  className="w-full mt-3 rounded-lg bg-[#0C0A0A]/70 border border-white/10 px-3 py-2 text-sm text-[#FBF8F3]" />
                <div className="flex gap-3 mt-2">
                  <Link to="/me" className="text-xs text-[#9E978E] hover:text-[#D4AF37] self-center">Edit my profile & photos →</Link>
                  <button data-testid={`my-submit-${r.request_id}`} disabled={busy === r.request_id} onClick={() => respond(r.request_id)}
                    className="ml-auto px-4 py-2 rounded-full bg-[#D4AF37] text-[#1a1113] text-sm font-semibold disabled:opacity-40">
                    {busy === r.request_id ? "Submitting…" : "Submit for review"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {data && data.open_requests.length === 0 && (
          <div className="mt-6 text-sm text-[#9E978E]" data-testid="my-app-norequests">
            {data.status === "resubmitted" ? "Thanks! Your response is under renewed review." : "No outstanding information requests right now."}
          </div>
        )}

        {data && data.all_requests.some((r) => r.status !== "open") && (
          <div className="mt-8">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#C9A96E] mb-2">History</h2>
            <div className="space-y-2">
              {data.all_requests.filter((r) => r.status !== "open").map((r) => (
                <div key={r.request_id} className="rounded-lg border border-white/8 p-3 text-sm">
                  <div className="text-[#FBF8F3]">{r.message}</div>
                  {r.response && <div className="text-[#9E978E] mt-1">Your response: {r.response}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
